import { useMemo } from 'react';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useCommonWidgetHandlers } from './use-common-widget-handlers';
import NavigateRequestModel from '../../../editor/features/build/requestModels/widgets/NavigateWidget';
import widgetCRUDActions from '@mm/state/actions/WidgetCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import WidgetModel, { NavigateConfig } from '@mm/state/model/WidgetModel';
import { PageKind } from '@mm/state/model/PageModel';
import * as Widgets from '@mm/constants/widgets';

const useDefaultDialogueId = () => {
  const dialogues = useSelector((state: StoreState) => state.page.entities);

  return useMemo(() => {
    const welcomeDialogue = dialogues.find(
      dialogue => dialogue.kind === PageKind.DefaultPage,
    );

    return welcomeDialogue?.id;
  }, [dialogues]);
};

export type AddNavigate = {
  dialogueId: number;
  sortOrder: number;
  associatedPageId?: number | null;
  oppositePageId?: number | null;
  sendNextAfterValue?: NavigateConfig['sendNextAfterValue'];
  sendNextAfterMeasure?: NavigateConfig['sendNextAfterMeasure'];
  subtype?: typeof Widgets.NAVIGATE | 'unicorn_detector';
};

export const useNavigateWidgetHandlers = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const commonWidgetHandlers = useCommonWidgetHandlers();
  const defaultDialogueId = useDefaultDialogueId();

  const addNavigate = ({
    dialogueId,
    sortOrder,
    associatedPageId,
    oppositePageId,
    sendNextAfterValue,
    sendNextAfterMeasure,
    subtype = Widgets.NAVIGATE,
  }: AddNavigate) => {
    const targetPageId = associatedPageId || defaultDialogueId;
    const widget = NavigateRequestModel.make({
      pageId: dialogueId,
      sortOrder,
      associatedPageId: targetPageId,
      oppositePageId,
      sendNextAfterValue,
      sendNextAfterMeasure,
      subtype,
    });
    return commonWidgetHandlers.create(widget, dialogueId, sortOrder);
  };

  const updateNavigateToDialogueId = (
    widget: WidgetModel,
    associatedPageId: number,
  ) => {
    dispatch(
      widgetCRUDActions.update({
        ...widget,
        config: {
          ...widget.config,
          pageId: associatedPageId,
        },
      }),
    );
  };

  const updateAudienceId = (
    widget: WidgetModel,
    audienceId: number | undefined | null,
  ) => {
    return dispatch(
      widgetCRUDActions.update({
        ...widget,
        config: {
          ...widget.config,
          audienceId,
        },
      }),
    );
  };

  return {
    delete: commonWidgetHandlers.delete,
    addNavigate,
    updateNavigateToDialogueId,
    updateAudienceId,
  };
};
